import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '@/store/rootReducer'

/**
 * licenseMgt slice
 *
 * Carries the in-progress license-creation context across the DAL action flow
 * and the tab navigations between them:
 *   LIC_Admin_Details_Save (creates the node → licId/licTypeId)
 *     → … navigate …
 *   LIC_Admin_License_Details_Save (reads licId/licTypeId to create the license)
 *
 * Client-side navigation keeps Redux state alive, so this is the natural place
 * to hold values that must survive a hop between tabs without a reload.
 */
export interface LicenseMgtState {
    licId: number | null
    licTypeId: number | null
}

const initialState: LicenseMgtState = {
    licId: null,
    licTypeId: null,
}

const licenseMgtSlice = createSlice({
    name: 'licenseMgt',
    initialState,
    reducers: {
        setLicenseContext: (
            state,
            action: PayloadAction<{ licId: number; licTypeId: number }>,
        ) => {
            state.licId = action.payload.licId
            state.licTypeId = action.payload.licTypeId
        },
        setLicTypeId: (state, action: PayloadAction<number>) => {
            state.licTypeId = action.payload
        },
        setLicId: (state, action: PayloadAction<number>) => {
            state.licId = action.payload
        },
        resetLicenseContext: () => initialState,
    },
})

export const { setLicenseContext, setLicTypeId, setLicId, resetLicenseContext } =
    licenseMgtSlice.actions

export const selectLicenseContext = (state: RootState): LicenseMgtState =>
    state.licenseMgt ?? initialState

export default licenseMgtSlice.reducer
